/**
 * INFINITY LIFE 2.0 — "Meu Dia" Dashboard Component
 */

import { store } from '../state.js';
import { api, showToast } from '../api.js';
import { getPriorityMeta, getLocationMeta, formatTimeBR } from '../utils/formatters.js';

export function renderDashboard() {
  const { dashboardData, tasks } = store.get();

  if (!dashboardData) {
    return `
      <div style="text-align: center; padding: 80px 20px;">
        <div style="font-size: 2.5rem; margin-bottom: 16px;">☀️</div>
        <h2>Carregando Meu Dia...</h2>
        <p style="color: var(--text-muted); margin-top: 8px;">Organizando compromissos e prioridades.</p>
      </div>
    `;
  }

  const { greeting, formatted_date, summary, next_upcoming, urgent_tasks, timeline } = dashboardData;
  const todayTasks = dashboardData.tasks || [];

  return `
    <div class="dashboard-view">
      <!-- Header Greeting & Date -->
      <div class="dashboard-header">
        <div>
          <h1 class="greeting-text">${greeting}</h1>
          <p class="date-text">${formatted_date}</p>
        </div>
        <div>
          <button class="btn-primary" id="btn-quick-new-task">
            <span>＋</span> <span>Nova Tarefa</span>
          </button>
        </div>
      </div>

      <!-- Quick Capture Bar -->
      <div class="quick-capture-card">
        <span style="font-size: 1.3rem;">⚡</span>
        <input type="text" id="quick-capture-input" class="quick-capture-input" 
               placeholder="Captura Rápida: Digite uma tarefa ou compromisso e pressione Enter..." />
        <button class="btn-secondary" id="btn-quick-capture-submit" style="padding: 6px 14px; font-size: 0.85rem;">
          Registrar
        </button>
      </div>

      <!-- Summary Metrics Grid -->
      <div class="metrics-grid">
        <!-- Metric 1: Progresso -->
        <div class="metric-card">
          <div class="metric-label">Progresso do Dia</div>
          <div class="metric-value">${summary.progress_percent}%</div>
          <div class="progress-bar-bg">
            <div class="progress-bar-fill" style="width: ${summary.progress_percent}%;"></div>
          </div>
          <div class="metric-sub">${summary.completed_tasks} de ${summary.total_tasks} tarefas concluídas</div>
        </div>

        <!-- Metric 2: Tarefas Pendentes -->
        <div class="metric-card">
          <div class="metric-label">Pendências Hoje</div>
          <div class="metric-value" style="color: ${summary.pending_tasks > 0 ? 'var(--text-primary)' : 'var(--emerald-accent)'};">
            ${summary.pending_tasks}
          </div>
          <div class="metric-sub">${summary.overdue_count > 0 ? `⚠️ ${summary.overdue_count} tarefas atrasadas` : 'Sem atrasos acumulados'}</div>
        </div>

        <!-- Metric 3: Saldo de Tempo -->
        <div class="metric-card">
          <div class="metric-label">Tempo Planejado</div>
          <div class="metric-value">${summary.estimated_time_hours}h</div>
          <div class="metric-sub">${summary.available_time_hours}h restantes na capacidade diária (8h)</div>
        </div>

        <!-- Metric 4: Foco / Sobrecarga -->
        <div class="metric-card" style="${summary.is_overloaded ? 'border-color: rgba(244, 63, 94, 0.4);' : ''}">
          <div class="metric-label">Status da Carga</div>
          <div class="metric-value" style="font-size: 1.4rem; color: ${summary.is_overloaded ? 'var(--rose-accent)' : 'var(--emerald-accent)'};">
            ${summary.is_overloaded ? 'Sobrecarga' : 'Equilibrado'}
          </div>
          <div class="metric-sub">
            ${summary.overload_alert || 'Carga de trabalho compatível com descanso saudável'}
          </div>
        </div>
      </div>

      <!-- 2-Column Section Layout -->
      <div class="dashboard-columns">
        <!-- Main Left Column -->
        <div class="dashboard-left">
          <!-- Next Upcoming Appointment -->
          ${nextUpcomingCard(next_upcoming)}

          <!-- Urgent & High Priority Tasks -->
          <div class="card-section">
            <div class="section-title">
              <span>🔥 Prioridades de Hoje</span>
              <span style="font-size: 0.85rem; color: var(--text-muted); font-weight: 500;">
                ${urgent_tasks.length} prioritárias
              </span>
            </div>

            ${urgent_tasks.length === 0 ? `
              <div style="text-align: center; padding: 24px 0; color: var(--text-muted);">
                <span>✨ Nenhuma tarefa urgente pendente hoje. Bom trabalho!</span>
              </div>
            ` : `
              <div class="task-list">
                ${urgent_tasks.map((t) => renderTaskRow(t)).join('')}
              </div>
            `}
          </div>

          <!-- All Tasks of Today -->
          <div class="card-section">
            <div class="section-title">
              <span>📋 Todas as Tarefas do Dia</span>
              <button class="btn-secondary" id="btn-view-all-agenda" style="font-size: 0.8rem; padding: 4px 10px;">
                Ver Agenda Completa →
              </button>
            </div>

            ${todayTasks.length === 0 ? `
              <div style="text-align: center; padding: 30px 0; color: var(--text-muted);">
                <p>Nenhuma tarefa cadastrada para hoje.</p>
                <button class="btn-primary" id="btn-empty-add-task" style="margin-top: 12px;">
                  Adicionar Tarefa
                </button>
              </div>
            ` : `
              <div class="task-list">
                ${todayTasks.map((t) => renderTaskRow(t)).join('')}
              </div>
            `}
          </div>
        </div>

        <!-- Right Column: Timeline & Contextual Advice -->
        <div class="dashboard-right">
          <!-- Timeline Card -->
          <div class="card-section">
            <div class="section-title">
              <span>⏱️ Linha do Tempo</span>
              <span style="font-size: 0.8rem; color: var(--text-muted);">Hoje</span>
            </div>

            <div class="timeline-list">
              ${renderTimelineSlots(timeline)}
            </div>
          </div>

          <!-- Dica do Momento (Copiloto) -->
          <div class="card-section" style="background: linear-gradient(145deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.04) 100%); border-color: rgba(99, 102, 241, 0.25);">
            <div class="section-title" style="color: var(--primary);">
              <span>💡 Dica do Momento</span>
            </div>
            <p style="font-size: 0.9rem; line-height: 1.6; color: var(--text-secondary);">
              ${summary.is_overloaded 
                ? 'Seu dia tem mais de 8 horas planejadas. Experimente escolher as 3 tarefas indispensáveis e adiar as opcionais.'
                : summary.completed_tasks > 0 && summary.pending_tasks === 0
                ? 'Todas as tarefas de hoje foram concluídas! Aproveite o tempo para descanso, família ou autocuidado.'
                : 'Mantenha pausas regulares entre blocos de trabalho para sustentar a clareza mental e a disposição.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  `;
}

function nextUpcomingCard(next) {
  if (!next) {
    return `
      <div class="card-section" style="padding: 18px 24px; background: rgba(99, 102, 241, 0.04); border-color: rgba(99, 102, 241, 0.2);">
        <div style="display: flex; align-items: center; justify-content: space-between;">
          <div style="display: flex; align-items: center; gap: 12px;">
            <span style="font-size: 1.5rem;">🎉</span>
            <div>
              <div style="font-size: 0.8rem; font-weight: 700; text-transform: uppercase; color: var(--primary);">Próximo Compromisso</div>
              <div style="font-size: 0.95rem; font-weight: 600; color: var(--text-primary); margin-top: 2px;">
                Nenhum compromisso agendado para o restante do dia.
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  const pMeta = getPriorityMeta(next.priority);
  const locMeta = getLocationMeta(next.context_location);

  return `
    <div class="card-section" style="padding: 20px 24px; border-color: rgba(99, 102, 241, 0.35); background: rgba(99, 102, 241, 0.06);">
      <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;">
        <span style="font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: var(--primary);">
          🎯 Próximo Foco Programado
        </span>
        ${next.due_time ? `
          <span style="font-family: var(--font-heading); font-weight: 700; font-size: 1.05rem; color: #ffffff;">
            ⏰ ${formatTimeBR(next.due_time)}
          </span>
        ` : ''}
      </div>

      <div style="font-size: 1.2rem; font-weight: 700; margin-bottom: 8px;">
        ${escapeHtml(next.title)}
      </div>

      <div style="display: flex; align-items: center; gap: 10px; font-size: 0.8rem;">
        <span class="badge ${pMeta.class}">${pMeta.icon} ${pMeta.label}</span>
        <span style="color: var(--text-muted);">${locMeta.icon} ${locMeta.label}</span>
        ${next.estimated_minutes > 0 ? `<span style="color: var(--text-muted);">⏱️ ${next.estimated_minutes} min</span>` : ''}
      </div>
    </div>
  `;
}

function renderTaskRow(task) {
  const isDone = task.status === 'completed';
  const pMeta = getPriorityMeta(task.priority);
  const locMeta = getLocationMeta(task.context_location);

  return `
    <div class="task-item ${isDone ? 'completed' : ''}" data-task-id="${task.id}">
      <div class="task-checkbox ${isDone ? 'checked' : ''}" data-action="toggle-task" data-id="${task.id}">
        ${isDone ? '✓' : ''}
      </div>

      <div class="task-info">
        <div class="task-title" data-action="edit-task" data-id="${task.id}">${escapeHtml(task.title)}</div>
        <div class="task-meta">
          <span class="badge ${pMeta.class}">${pMeta.label}</span>
          ${task.due_time ? `<span>⏰ ${formatTimeBR(task.due_time)}</span>` : ''}
          ${task.estimated_minutes > 0 ? `<span>⏱️ ${task.estimated_minutes} min</span>` : ''}
          <span>${locMeta.icon} ${locMeta.label}</span>
          ${task.category_name ? `<span style="color: ${task.category_color || 'var(--text-muted)'};">🏷️ ${escapeHtml(task.category_name)}</span>` : ''}
        </div>
      </div>

      <div class="task-actions" style="display: flex; gap: 4px;">
        <button class="btn-icon" data-action="edit-task" data-id="${task.id}" title="Editar" style="width: 32px; height: 32px; font-size: 0.85rem;">
          ✏️
        </button>
      </div>
    </div>
  `;
}

function renderTimelineSlots(timelineMap = {}) {
  const slots = [8, 9, 10, 11, 14, 15, 16, 17, 19, 20];

  return slots.map((hour) => {
    const hourKey = hour;
    const hourStr = `${String(hour).padStart(2, '0')}:00`;
    const items = timelineMap[hourKey] || [];

    return `
      <div class="timeline-slot">
        <div class="timeline-dot" style="${items.length > 0 ? 'background: var(--primary);' : ''}"></div>
        <div class="timeline-hour">${hourStr}</div>
        <div class="timeline-content" style="${items.length > 0 ? 'border-color: rgba(99, 102, 241, 0.3);' : 'opacity: 0.6;'}">
          ${items.length === 0 ? `
            <span style="font-size: 0.8rem; color: var(--text-muted);">Disponível</span>
          ` : `
            ${items.map((it) => `
              <div style="font-weight: 600; font-size: 0.88rem; color: var(--text-primary); margin-bottom: 2px;">
                ${escapeHtml(it.title)}
              </div>
              <div style="font-size: 0.75rem; color: var(--text-muted);">
                ${it.time} • ${it.category}
              </div>
            `).join('')}
          `}
        </div>
      </div>
    `;
  }).join('');
}

function escapeHtml(str) {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

export function attachDashboardEvents(container) {
  // Quick Capture Input
  const quickInput = container.querySelector('#quick-capture-input');
  const quickBtn = container.querySelector('#btn-quick-capture-submit');

  const handleQuickCapture = async () => {
    const title = quickInput.value.trim();
    if (!title) return;

    try {
      await api.createTask({
        title,
        priority: 'medium',
        due_date: new Date().toISOString().split('T')[0],
      });
      quickInput.value = '';
      showToast('Tarefa registrada com sucesso!', 'success');
      window.dispatchEvent(new CustomEvent('infinity:refresh-data'));
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  if (quickInput) {
    quickInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') handleQuickCapture();
    });
  }
  if (quickBtn) {
    quickBtn.addEventListener('click', handleQuickCapture);
  }

  // New Task button
  const newBtn = container.querySelector('#btn-quick-new-task') || container.querySelector('#btn-empty-add-task');
  if (newBtn) {
    newBtn.addEventListener('click', () => store.openTaskModal());
  }

  // Go to full agenda
  const agendaBtn = container.querySelector('#btn-view-all-agenda');
  if (agendaBtn) {
    agendaBtn.addEventListener('click', () => store.setView('agenda'));
  }

  // Task Toggle
  container.querySelectorAll('[data-action="toggle-task"]').forEach((el) => {
    el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = el.dataset.id;
      try {
        await api.toggleTask(id);
        window.dispatchEvent(new CustomEvent('infinity:refresh-data'));
      } catch (err) {
        showToast(err.message, 'error');
      }
    });
  });

  // Edit Task
  container.querySelectorAll('[data-action="edit-task"]').forEach((el) => {
    el.addEventListener('click', async () => {
      const id = el.dataset.id;
      try {
        const res = await api.getTask(id);
        if (res.success) {
          store.openTaskModal(res.data);
        }
      } catch (err) {
        showToast(err.message, 'error');
      }
    });
  });
}
